import { useHead } from "@unhead/vue";
import { computed } from "vue";
import { useRoute } from "vue-router";
import FeatureFlags from "@/core/utilities/featureFlags";
import { useThemeContext } from "./useThemeContext";

type HeadLinkType = { rel: string; href: string; hreflang?: string };

type HreflangDomainType = { readonly hreflang: string; readonly origin: string; readonly pathPrefix: string };

type EnvironmentConfigType = {
  readonly origins: readonly string[];
  readonly hreflangDomains: readonly HreflangDomainType[];
  readonly xDefault: string;
};

const ENVIRONMENT_CONFIGS: readonly EnvironmentConfigType[] = [
  {
    origins: ["https://www.thorlabs.com", "https://www.thorlabschina.cn", "https://www.thorlabs.co.jp"],
    hreflangDomains: [
      { hreflang: "en", origin: "https://www.thorlabs.com", pathPrefix: "" },
      { hreflang: "zh-CN", origin: "https://www.thorlabschina.cn", pathPrefix: "/zh" },
      { hreflang: "zh", origin: "https://www.thorlabs.com", pathPrefix: "/zh" },
      { hreflang: "ja", origin: "https://www.thorlabs.co.jp", pathPrefix: "/ja" },
    ],
    xDefault: "https://www.thorlabs.com",
  },
  {
    origins: [
      "https://thorlabs-prep4us-store.govirto.com",
      "https://thorlabs-prep4jp-store.govirto.com",
      "https://thorlabs-prep4cn-store.thorlabsazure.cn",
    ],
    hreflangDomains: [
      { hreflang: "en", origin: "https://thorlabs-prep4us-store.govirto.com", pathPrefix: "" },
      { hreflang: "zh-CN", origin: "https://thorlabs-prep4cn-store.thorlabsazure.cn", pathPrefix: "/zh" },
      { hreflang: "zh", origin: "https://thorlabs-prep4us-store.govirto.com", pathPrefix: "/zh" },
      { hreflang: "ja", origin: "https://thorlabs-prep4jp-store.govirto.com", pathPrefix: "/ja" },
    ],
    xDefault: "https://thorlabs-prep4us-store.govirto.com",
  },
] as const;

const getEnvironmentConfig = (origin: string): EnvironmentConfigType | undefined =>
  ENVIRONMENT_CONFIGS.find((config) => config.origins.includes(origin));

const NON_INDEXABLE_PATH_PREFIXES = [
  "/cart",
  "/checkout",
  "/account",
  "/sign-in",
  "/sign-up",
  "/reset-password",
  "/set-password",
  "/forgot-password",
  "/forgot-id",
  "/confirm-invitation",
  "/change-password",
  "/blocked",
  "/404",
  "/403",
  "/500",
  "/search",
] as const;

const LOCALE_PREFIXES = ["/zh", "/ja", "/de", "/en"] as const;

const getLocaleStrippedPath = (path: string): string => {
  for (const prefix of LOCALE_PREFIXES) {
    if (path === prefix) {
      return "";
    }
    if (path.startsWith(`${prefix}/`)) {
      return path.slice(prefix.length + 1);
    }
  }
  return path.startsWith("/") ? path.slice(1) : path;
};

const isIndexablePath = (path: string): boolean => {
  const normalizedPath = `/${getLocaleStrippedPath(path)}`;
  return !NON_INDEXABLE_PATH_PREFIXES.some(
    (prefix) => normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`),
  );
};

export const useHreflangCanonical = (): void => {
  const route = useRoute();
  const { themeContext } = useThemeContext();

  useHead({
    link: computed<HeadLinkType[]>(() => {
      const path = route.path;

      if (!isIndexablePath(path)) {
        return [];
      }

      const isHreflangEnabled = new FeatureFlags(themeContext.value?.storeSettings?.modules ?? []).isFeatureEnabled(
        "Thorlabs.Commerce",
        "EnableHreflang",
      );

      if (!isHreflangEnabled) {
        return [];
      }

      const canonicalHref = `${window.location.origin}${window.location.pathname}`;
      const links: HeadLinkType[] = [{ rel: "canonical", href: canonicalHref }];

      const envConfig = getEnvironmentConfig(window.location.origin);

      if (envConfig) {
        const strippedPath = getLocaleStrippedPath(path);
        for (const domain of envConfig.hreflangDomains) {
          links.push({
            rel: "alternate",
            hreflang: domain.hreflang,
            href: `${domain.origin}${domain.pathPrefix}/${strippedPath}`,
          });
        }
        links.push({ rel: "alternate", hreflang: "x-default", href: `${envConfig.xDefault}/${strippedPath}` });
      }

      return links;
    }),
  });
};
